import React, { Fragment, useState } from "react";
import { ResponsiveLine } from '@nivo/line';
import { useQuery } from "react-query";
import {
  TextField,
  MenuItem,
  FormControl,
  Select,
  Card,
  CardHeader,
  CardContent,
  Typography,
  InputLabel,
  Paper,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { getDeploymentsByPeriod } from "../../services/Dashboard";
import { getLast365DaysDateRange } from "../../services/Utils";
import { useTheme } from "@mui/material/styles";
import moment from "moment";

const PRIMARY_OPTIONS = [
  { value: "last_365_days", label: "Last 365 Days" },
  { value: "current_year", label: "This Year" },
  { value: "date_range", label: "Date Range" },
];

const DeploymentsByPeriod = () => {
  const last365DaysDateRange = getLast365DaysDateRange();
  const [period, setPeriod] = useState(new Date().getFullYear());
  const [primaryFilter, setPrimaryFilter] = useState("last_365_days");
  const handlePrimaryFilterChange = ({ target: { value } }) => {
    setPrimaryFilter(value);
    if (value === "current_year") {
      setPeriod(new Date().getFullYear());
    } else if (value === "date_range") {
      setPeriod(typeof period === 'number' ? last365DaysDateRange : period);
    } else if (value === "last_365_days") {
      setPeriod(last365DaysDateRange);
    }
  };

  const handleDateChange = (newValue, type) => {
    setPeriod({
      ...period,
      ...(type === "start" && { from: newValue }),
      ...(type === "end" && { to: newValue }),
    });
  };

  const theme = useTheme();
  const commonProperties = {
    margin: { top: 80, right: 120, bottom: 80, left: 120 },
    colors: [theme.custom.barChartColor],
    animate: false,
    activeOuterRadiusOffset: 8,
    legends: [
      {
        dataFrom: "keys",
        anchor: "bottom-right",
        direction: "column",
        justify: false,
        translateX: 120,
        translateY: 0,
        itemsSpacing: 2,
        itemWidth: 100,
        itemHeight: 20,
        itemDirection: "left-to-right",
        itemOpacity: 1,
        symbolSize: 20,
        effects: [
          {
            on: "hover",
            style: {
              itemOpacity: 1,
            },
          },
        ],
      },
    ],
    axisLeft: { tickValues: [] },
    axisBottom:
      !!primaryFilter && primaryFilter === "date_range"
        ? null
        : { tickRotation: -60 }, // null hides the bottom label // { tickRotation: -60 } use this to rotate bottom label,
    enableLabel:
      !!primaryFilter && primaryFilter === "date_range" ? false : true,
  };

  const validatePeriodData = () => {
    if (primaryFilter === 'date_range') {
      return !!(period?.from && period?.to);
    }
    return true;
  }

  const { data = [], isLoading } = useQuery(
    ["deploymentsByPeriod", primaryFilter, period],
    getDeploymentsByPeriod,
    {
      refetchOnWindowFocus: false,
      enabled: validatePeriodData()
    }
  );

  const CustomTooltip = ({ point: { data: { x, y } = {} } = {} }) => {
    return (
      <Paper
        style={{
          padding: "6px",
        }}
      >
        <div>
          <Typography
            color="text.primary"
            sx={{ fontSize: 16, textAlign: "left" }}
          >
            {x}
          </Typography>
          <Typography
            color="text.secondary"
            sx={{ fontSize: 12, textAlign: "left" }}
          >{` ${y} deployment${y > 1 ? "s" : ""}`}</Typography>
        </div>
      </Paper>
    );
  };

  const disablePastOneYearDates = (date) => {
    const date1 = moment(new Date(period.to)).subtract(1, 'year');
    const date2 = moment(new Date(date));
    return date2.isSameOrBefore(date1);
  }

  return (
    <Card variant="outlined">
      <CardHeader
        title={
          <Typography color="text.secondary">Deployments by period</Typography>
        }
        action={
          <Fragment>
            {primaryFilter === "date_range" && (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }}>
                  <DatePicker
                    label="From"
                    value={period?.from}
                    onChange={(val) => handleDateChange(val, "start")}
                    renderInput={(params) => (
                      <TextField
                        size="small"
                        {...params}
                        id="standard-basic"
                        variant="standard"
                      />
                    )}
                    required
                    inputFormat="dd-MM-yyyy"
                    disableFuture
                    shouldDisableDate={disablePastOneYearDates}
                  />
                </FormControl>
                <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }}>
                  <DatePicker
                    label="To"
                    value={period?.to}
                    onChange={(val) => handleDateChange(val, "end")}
                    disableFuture
                    renderInput={(params) => (
                      <TextField
                        size="small"
                        {...params}
                        id="standard-basic"
                        variant="standard"
                      />
                    )}
                    minDateTime={period?.from}
                    required
                    inputFormat="dd-MM-yyyy"
                  />
                </FormControl>
              </LocalizationProvider>
            )}
            <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }}>
              <InputLabel id="demo-simple-select-standard-label">
                Filter
              </InputLabel>
              <Select
                label="Filter"
                labelId="demo-simple-select-standard-label"
                id="demo-simple-select-standard"
                value={primaryFilter}
                onChange={handlePrimaryFilterChange}
              >
                {PRIMARY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Fragment>
        }
      ></CardHeader>
      <CardContent style={{ height: "350px" }}>
        {data?.data?.length > 0 ? (
          <>
            {!isLoading && (
              <ResponsiveLine
                colors={[theme.palette.primary[600]]}
                data={[data]}
                margin={{
                  bottom: 60,
                  left: 50,
                  right: 20,
                  top: 10
                }}
                xScale={{ type: 'point' }}
                tooltip={CustomTooltip}
                yScale={{
                  type: 'linear',
                  min: 'auto',
                  max: 'auto',
                  stacked: true,
                  reverse: false
                }}
                yFormat=" >-.2f"
                axisTop={null}
                axisRight={null}
                axisBottom={primaryFilter === 'date_range'
                  ? {
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 45,
                    legend: "Months",
                    legendOffset: 50,
                    legendPosition: 'middle',
                    truncateTickAt: 0.9
                  } : {
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    legend: "Months",
                    legendOffset: 50,
                    legendPosition: 'middle',
                    truncateTickAt: 0
                  }}
                axisLeft={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: 'Count',
                  legendOffset: -40,
                  legendPosition: 'middle',
                  truncateTickAt: 0
                }}
                pointSize={10}
                pointColor={{ theme: 'background' }}
                pointBorderWidth={2}
                pointBorderColor={{ from: 'serieColor' }}
                pointLabel="data.yFormatted"
                pointLabelYOffset={-12}
                enableTouchCrosshair={true}
                useMesh={true}
                legends={[
                  {
                    anchor: 'bottom-right',
                    direction: 'row',
                    itemHeight: 10,
                    itemWidth: 80,
                    toggleSerie: true,
                    translateY: 50
                  }
                ]}
              />
            )}
          </>
        ) : (
          <>
            {!isLoading && (
              <ResponsiveLine
                data={[]}
                {...commonProperties}
                tooltip={CustomTooltip}
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

DeploymentsByPeriod.defaultProps = {};

DeploymentsByPeriod.propTypes = {};

export default DeploymentsByPeriod;
